import { zodResolver } from "@hookform/resolvers/zod";
import {
  Card,
  CardBody,
  CardFooter,
  Dialog,
  Input,
  Option,
  Select,
  Typography,
  Checkbox,
} from "@material-tailwind/react";
import Label from "components/Label/Label";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import { fetchFeatures } from "redux/features/Admin/PlanFeature/planFeatureSlice";
import { createPlan } from "redux/features/subscription/subscriptionSlice";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { z } from "zod";

// Define the Feature interface to type featuresList
interface Feature {
  id: number;
  description: string;
}

// Zod schema for plan data
const planSchema = z.object({
  name: z.string().min(1, "Le nom est requis"),
  amount: z.number().nonnegative("Le montant doit être positif"),
  trial_periode: z.number().nonnegative("La période d'essai doit être positive"),
  interval: z.enum(["day", "week", "month", "year"], {
    errorMap: () => ({ message: "L'intervalle doit être l'un des suivants : jour, semaine, mois, année" }),
  }),
  features: z
    .array(
      z.object({
        id: z.number(),
        value: z.string().min(1, "La valeur de la fonctionnalité est requise"),
      })
    )
    .optional(),
});

type PlanSchemaType = z.infer<typeof planSchema>;

// Animation variants
const dialogVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: "easeOut" } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2, ease: "easeIn" } },
};

const fieldVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

interface EntityDialogFormProps {
  open: boolean;
  handleClose?: () => void;
  onSubmitSuccess?: () => void;
  isLoading?: boolean;
  addfn?: typeof createPlan;
  addfnTitle?: string;
}

export const PlanDialogForm: React.FC<EntityDialogFormProps> = React.memo(
  ({
    addfn,
    open,
    addfnTitle = "Ajouter un forfait",
    onSubmitSuccess,
    handleClose = () => {},
    isLoading = false,
  }) => {
    const {
      register,
      handleSubmit,
      formState: { errors },
      reset,
      setValue,
      control,
      watch,
    } = useForm<PlanSchemaType>({
      resolver: zodResolver(planSchema),
      defaultValues: {
        name: "",
        amount: 0,
        interval: "year",
        trial_periode: 0,
        features: [],
      },
    });
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    // Type the featuresList as an array of Feature
    const featuresList = useAppSelector((state) => state.planFeature.features) as Feature[];

    // Reset form when dialog opens/closes
    useEffect(() => {
      if (open) {
        reset({
          name: "",
          amount: 0,
          interval: "year",
          trial_periode: 0,
          features: [],
        });
      }
    }, [open, reset]);

    useEffect(() => {
      dispatch(fetchFeatures());
    }, [dispatch]);

    const onSubmit = async (data: PlanSchemaType) => {
      try {
        setLoading(true);
        if (addfn) {
          await dispatch(addfn({ data })).unwrap();
          toast.success("Plan créé avec succès");
        }
        onSubmitSuccess?.();
        handleClose();
        reset();
      } catch (error: any) {
        toast.error(error.message || "Une erreur est survenue");
      } finally {
        setLoading(false);
      }
    };

    // Watch selected features to dynamically show input fields
    const selectedFeatures = watch("features") || [];

    return (
      <AnimatePresence>
        {open && (
          <Dialog
            size="sm"
            open={open}
            handler={handleClose}
            className="w-full max-w-md mx-auto transition-all duration-300 bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
            aria-labelledby="dialog-title"
          >
            <motion.div variants={dialogVariants} initial="hidden" animate="visible" exit="exit">
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Card className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
                  <CardBody className="flex flex-col gap-6 p-6 max-h-[50vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-t-2xl">
                    <Typography
                      id="dialog-title"
                      variant="h5"
                      className="p-4 font-bold tracking-tight text-center text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl animate-fade-in"
                    >
                      {addfnTitle}
                    </Typography>
                    {/* Name */}
                    <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                      <Label className="font-semibold text-gray-700 dark:text-gray-200">Nom :</Label>
                      <div className="relative mt-2">
                        <Input
                          size="lg"
                          type="text"
                          placeholder="Ex. Plan Premium"
                          {...register("name")}
                          aria-invalid={!!errors.name}
                          aria-describedby="name-error"
                          className="text-gray-900 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white hover:shadow-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                      </div>
                      <AnimatePresence>
                        {errors.name && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <Typography
                              id="name-error"
                              variant="small"
                              color="red"
                              className="mt-1 text-xs text-red-500 dark:text-red-400"
                            >
                              {errors.name.message}
                            </Typography>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                    {/* Amount */}
                    <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                      <Label className="font-semibold text-gray-700 dark:text-gray-200">Montant :</Label>
                      <div className="relative mt-2">
                        <Input
                          size="lg"
                          type="text"
                          step="0.01"
                          placeholder="Ex. 99.99"
                          {...register("amount", { valueAsNumber: true })}
                          aria-invalid={!!errors.amount}
                          aria-describedby="amount-error"
                          className="text-gray-900 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white hover:shadow-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                      </div>
                      <AnimatePresence>
                        {errors.amount && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <Typography
                              id="amount-error"
                              variant="small"
                              color="red"
                              className="mt-1 text-xs text-red-500 dark:text-red-400"
                            >
                              {errors.amount.message}
                            </Typography>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                    {/* Interval */}
                    <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                      <Label className="font-semibold text-gray-700 dark:text-gray-200">Type :</Label>
                      <div className="relative mt-2">
                        <Select
                          {...register("interval")}
                          onChange={(value) => setValue("interval", value as "day" | "week" | "month" | "year")}
                          defaultValue="year"
                          className="text-gray-900 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white hover:shadow-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                          aria-invalid={!!errors.interval}
                          aria-describedby="interval-error"
                        >
                          <Option value="day">Jour</Option>
                          <Option value="week">Semaine</Option>
                          <Option value="month">Mois</Option>
                          <Option value="year">Année</Option>
                        </Select>
                      </div>
                      <AnimatePresence>
                        {errors.interval && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <Typography
                              id="interval-error"
                              variant="small"
                              color="red"
                              className="mt-1 text-xs text-red-500 dark:text-red-400"
                            >
                              {errors.interval.message}
                            </Typography>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                    {/* Trial Period */}
                    <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                      <Label className="font-semibold text-gray-700 dark:text-gray-200">Période d'essai :</Label>
                      <div className="relative mt-2">
                        <Input
                          size="lg"
                          type="text"
                          step="0.01"
                          placeholder="Ex. 99.99"
                          {...register("trial_periode", { valueAsNumber: true })}
                          aria-invalid={!!errors.trial_periode}
                          aria-describedby="trial_periode-error"
                          className="text-gray-900 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white hover:shadow-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                      </div>
                      <AnimatePresence>
                        {errors.trial_periode && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <Typography
                              id="trial_periode-error"
                              variant="small"
                              color="red"
                              className="mt-1 text-xs text-red-500 dark:text-red-400"
                            >
                              {errors.trial_periode.message}
                            </Typography>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                    {/* Features Selection */}
                    <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                      <Label className="font-semibold text-gray-700 dark:text-gray-200">Caractéristiques :</Label>
                      <div className="mt-2 space-y-4">
                        {featuresList.map((feature) => (
                          <div key={feature.id} className="flex flex-col gap-2">
                            <Controller
                              name="features"
                              control={control}
                              render={({ field }) => {
                                const isChecked = field.value?.some((f) => f.id === feature.id) || false;
                                return (
                                  <div className="flex items-center">
                                    <Checkbox
                                      id={`feature-${feature.id}`}
                                      checked={isChecked}
                                      onChange={(e) => {
                                        const currentFeatures = field.value || [];
                                        if (e.target.checked) {
                                          field.onChange([...currentFeatures, { id: feature.id, value: "" }]);
                                        } else {
                                          field.onChange(currentFeatures.filter((f) => f.id !== feature.id));
                                        }
                                      }}
                                      className="text-blue-500"
                                    />
                                    <Label className="text-gray-700 dark:text-gray-200">
                                      {feature.description}
                                    </Label>
                                  </div>
                                );
                              }}
                            />
                            <AnimatePresence>
                              {selectedFeatures.some((f) => f.id === feature.id) && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="ml-8"
                                >
                                  <Input
                                    size="lg"
                                    type="text"
                                    placeholder="Valeur de la fonctionnalité"
                                    value={selectedFeatures.find((f) => f.id === feature.id)?.value || ""}
                                    onChange={(e) => {
                                      const currentFeatures = selectedFeatures.map((f) =>
                                        f.id === feature.id ? { ...f, value: e.target.value } : f
                                      );
                                      setValue("features", currentFeatures);
                                    }}
                                    aria-describedby={`feature-value-error-${feature.id}`}
                                    className="text-gray-900 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white hover:shadow-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                                  />
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  </CardBody>
                  <CardFooter className="flex justify-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
                    <ButtonSecondary
                      type="button"
                      onClick={handleClose}
                      className="px-6 py-2 text-gray-900 transition-colors duration-200 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
                      disabled={loading || isLoading}
                    >
                      Annuler
                    </ButtonSecondary>
                    <ButtonSecondary
                      type="submit"
                      className="px-6 py-2 text-white transition-all duration-300 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 hover:from-blue-600 hover:to-indigo-700 dark:hover:from-blue-700 dark:hover:to-indigo-800 hover:shadow-lg"
                      disabled={loading || isLoading}
                      loading={loading || isLoading}
                    >
                      Confirmer
                    </ButtonSecondary>
                  </CardFooter>
                </Card>
              </form>
            </motion.div>
          </Dialog>
        )}
      </AnimatePresence>
    );
  }
);
